'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Close } from '@repo/ui/components/close'
import { cn } from '@repo/ui/lib/utils'
import { cva, VariantProps } from 'class-variance-authority'
import { ComponentPropsWithoutRef, createContext, useContext } from 'react'

const DialogContext = createContext<{ modal: boolean }>({ modal: true })

function Dialog({ modal = true, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Root>) {
  return (
    <DialogContext.Provider value={{ modal }}>
      <DialogPrimitive.Root data-slot='dialog' modal={modal} {...props} />
    </DialogContext.Provider>
  )
}

function DialogTrigger({ ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

function DialogPortal({ ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

function DialogOverlay({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot='dialog-overlay'
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60',
        className,
      )}
      {...props}
    />
  )
}

const dialogContentVariants = cva(
  'data-[state=open]:fade-in data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex w-full max-w-[calc(100vw-(var(--spacing-miista-page-padding)_*_2))] flex-col gap-3 rounded-lg p-3 text-black duration-200 sm:max-w-md',
  {
    variants: {
      background: {
        white: 'border-border border border-solid bg-white',
        purple: 'bg-miista-purple-50',
      },
      position: {
        center: 'data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-bottom left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
        bottomLeft:
          'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left left-miista-page-padding bottom-[var(--spacing-miista-bottom-offset,--spacing-miista-page-padding)] translate-x-0 translate-y-0',
        bottomRight:
          'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right right-miista-page-padding bottom-[var(--spacing-miista-bottom-offset,--spacing-miista-page-padding)] translate-x-0 translate-y-0',
      },
      close: {
        cross: '',
        none: '',
        // Please note the punch hole is manually aligned to the same top-right position of the close button.
        punchHole: 'is-hole group [mask-image:radial-gradient(circle_0.6rem_at_calc(100%_-_1.2rem)_1.2rem,_transparent_100%,_black_100%)]',
      },
    },
    defaultVariants: {
      background: 'white',
      position: 'center',
      close: 'cross',
    },
  },
)

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, VariantProps<typeof dialogContentVariants> {
  closePosition?: 'right' | 'left'
}

function DialogContent({ className, children, position, background, close = 'cross', closePosition = 'right', ...props }: DialogContentProps) {
  const { modal } = useContext(DialogContext)

  return (
    <DialogPortal data-slot='dialog-portal'>
      {modal && <DialogOverlay />}
      <DialogPrimitive.Content
        data-slot='dialog-content'
        className={cn(dialogContentVariants({ position, background, close }), !modal && 'focus-visible:outline-hidden', className)}
        onOpenAutoFocus={(e) => !modal && e.preventDefault()}
        onPointerDownOutside={(e) => !modal && e.preventDefault()}
        onInteractOutside={(e) => !modal && e.preventDefault()}
        {...props}
      >
        {children}
        {close != 'none' && (
          <DialogPrimitive.Close
            className={cn(
              'focus-visible:miista-focus group absolute top-2 flex size-5 items-center justify-center rounded-sm disabled:pointer-events-none group-[.is-hole]:opacity-0 [&_svg]:pointer-events-none',
              closePosition === 'left' && 'left-2',
              closePosition === 'right' && 'right-2',
            )}
          >
            <Close />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div data-slot='dialog-header' className={cn('flex flex-col gap-2 pr-12', className)} {...props} />
}

function DialogAside({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div data-slot='dialog-aside' className={cn('flex w-28 shrink-0', className)} {...props} />
}

function DialogFooter({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div data-slot='dialog-footer' className={cn('mb-0 mt-auto flex items-end justify-end gap-3', className)} {...props} />
}

function DialogTitle({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot='dialog-title' className={cn('uppercase', className)} {...props} />
}

function DialogDescription({ className, ...props }: ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot='dialog-description' className={cn(className)} {...props} />
}

export { Dialog, DialogAside, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger }
